import React, { PropsWithoutRef } from "react";
import { ReactElement } from "rehype-react/lib";
import styled from "styled-components";
import tw from "twin.macro";

import GetADemoButton from "../buttons/GetADemoButton";
import Container from "./Container";
import Section, { SectionColor, SectionContent } from "./Section";

const CtaHeadline = styled.div`
  ${tw`font-title font-normal text-inherit text-white`}

  font-size: 46px;
  line-height: 60px;
`;

const CtaHeadlineHeadlineHighlight = styled.span`
  ${tw`text-theme-blue-1 font-bold`}
`;

export const AutopilotSlogan: React.FunctionComponent = () => (
  <CtaHeadline>
    Put customer retention on{" "}
    <CtaHeadlineHeadlineHighlight>Autopilot</CtaHeadlineHeadlineHighlight>
  </CtaHeadline>
);

export const GrowMrrSlogan: React.FunctionComponent = () => (
  <CtaHeadline>
    <CtaHeadlineHeadlineHighlight>Grow MRR faster</CtaHeadlineHeadlineHighlight>{" "}
    with ProsperStack
  </CtaHeadline>
);

export const IntegrationStripeSlogan: React.FunctionComponent<{}> = () => (
  <CtaHeadline>
    <CtaHeadlineHeadlineHighlight>
      Reduce Stripe churn
    </CtaHeadlineHeadlineHighlight>{" "}
    with ProsperStack
  </CtaHeadline>
);

export const IntegrationNavigaSlogan: React.FunctionComponent<{}> = () => (
  <CtaHeadline>
    <CtaHeadlineHeadlineHighlight>More saves</CtaHeadlineHeadlineHighlight> with
    ProsperStack + Naviga
  </CtaHeadline>
);

export const IntegrationSlogan: React.FunctionComponent<{
  integrationName: string;
}> = ({ integrationName }) => (
  <CtaHeadline>
    <CtaHeadlineHeadlineHighlight>Reduce churn</CtaHeadlineHeadlineHighlight>{" "}
    with ProsperStack + {integrationName}
  </CtaHeadline>
);

export const CustomerPortalSlogan: React.FunctionComponent = () => (
  <CtaHeadline>
    <CtaHeadlineHeadlineHighlight>Grow MRR</CtaHeadlineHeadlineHighlight> with a
    free retention-optimized customer portal
  </CtaHeadline>
);

export const SaasSlogan: React.FunctionComponent = () => (
  <CtaHeadline>
    Take the{" "}
    <CtaHeadlineHeadlineHighlight>next step</CtaHeadlineHeadlineHighlight> for
    your SaaS
  </CtaHeadline>
);

export const EcommerceSlogan: React.FunctionComponent = () => (
  <CtaHeadline>
    Take the{" "}
    <CtaHeadlineHeadlineHighlight>next step</CtaHeadlineHeadlineHighlight> for
    your ecommerce subscription business
  </CtaHeadline>
);

export const DigitalMediaSlogan: React.FunctionComponent = () => (
  <CtaHeadline>
    Take the{" "}
    <CtaHeadlineHeadlineHighlight>next step</CtaHeadlineHeadlineHighlight> for
    your digital media company
  </CtaHeadline>
);

const Content = styled.div`
  ${tw`flex flex-col gap-6 text-center content-center justify-center items-center`}

  &:not(.stacked) {
    @media (min-width: 1020px) {
      ${tw`flex-row text-left justify-between`}
    }
  }
`;

interface CtaSectionProps {
  slogan?: ReactElement;
  button?: ReactElement;
}

const FooterCta: React.FunctionComponent<PropsWithoutRef<CtaSectionProps>> = ({
  slogan = <GrowMrrSlogan />,
  button = <GetADemoButton />,
  ...props
}) => (
  <Container {...props}>
    <Section color={SectionColor.Black}>
      <SectionContent tw="pt-0">
        <Content>
          {slogan}
          {button}
        </Content>
      </SectionContent>
    </Section>
  </Container>
);

export default FooterCta;
